import type { Session } from '@supabase/supabase-js';
import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';

export type SessionState = {
  session: Session | null;
  loading: boolean;
  /**
   * WHO is signed in — the same string for as long as they are. Key a hook's
   * dependencies on this, never on `session` (2026-10-11).
   */
  userId: string | null;
};

/**
 * Hook returning the current Supabase auth session.
 *
 * `loading` is true until the initial session fetch resolves. After that,
 * the session updates reactively via `onAuthStateChange`.
 *
 * 🔴 `session` IS A NEW OBJECT FOR THE SAME VISITOR, OFTEN. auth-js 2.103
 * replaces it on every token refresh and EVERY TIME THE TAB COMES BACK INTO
 * VIEW (it re-reads the stored session and announces SIGNED_IN). A hook keyed
 * on `session` re-runs each time: the lot page went back to "Loading lot…"
 * and made 19 requests whenever a bidder switched tabs. Key on `userId`; read
 * the token when a request needs it (`currentAccessToken`) — the Supabase
 * client already sends it on its own calls. Guarded by
 * `sessionKeyedHooks.structure.test.ts`.
 */
export function useSession(): SessionState {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    void supabase.auth.getSession().then(({ data }) => {
      if (!isMounted) return;
      setSession(data.session);
      setLoading(false);
    });

    const { data: subscription } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      if (!isMounted) return;
      setSession(nextSession);
    });

    return () => {
      isMounted = false;
      subscription.subscription.unsubscribe();
    };
  }, []);

  return { session, loading, userId: session?.user.id ?? null };
}

/**
 * Role values recognized by the platform — mirrors the `role` check constraint
 * on `public.user_roles` (migration 0001).
 */
export type AppRole = 'super_admin' | 'tenant_owner' | 'tenant_staff' | 'consignor';

/**
 * Claims extracted from the Supabase JWT's `app_metadata`, populated by the
 * Custom Access Token Hook (migrations 0002 + 0003). The hook writes:
 *
 *   app_metadata.is_super_admin : boolean
 *   app_metadata.tenant_id      : string | undefined  (primary tenant)
 *   app_metadata.tenant_roles   : {
 *     [tenantUuid | "platform"]: AppRole[]
 *   }
 *
 * `appRole` is a derived "primary role" for simple gating:
 *   super_admin > tenant_owner > tenant_staff > consignor > null
 */
export type AppClaims = {
  appRole: AppRole | null;
  tenantId: string | null;
  isSuperAdmin: boolean;
  tenantRoles: Record<string, AppRole[]>;
};

type JwtAppMetadata = {
  is_super_admin?: boolean;
  tenant_id?: string | null;
  tenant_roles?: Record<string, AppRole[]>;
};

type JwtPayload = {
  app_metadata?: JwtAppMetadata;
};

/**
 * Roles recognized at the app level. Anything else the hook emits is ignored.
 */
const KNOWN_ROLES: readonly AppRole[] = [
  'super_admin',
  'tenant_owner',
  'tenant_staff',
  'consignor',
];

const ROLE_PRIORITY: readonly AppRole[] = [
  'super_admin',
  'tenant_owner',
  'tenant_staff',
  'consignor',
];

function pickPrimaryRole(
  isSuperAdmin: boolean,
  tenantRoles: Record<string, AppRole[]>,
  primaryTenantId: string | null,
): AppRole | null {
  if (isSuperAdmin) return 'super_admin';

  const candidates: AppRole[] = [];
  const tenantScoped = primaryTenantId ? (tenantRoles[primaryTenantId] ?? []) : [];
  candidates.push(...tenantScoped);
  // Fall back to any role across any tenant if the primary tenant has no roles
  if (candidates.length === 0) {
    for (const roles of Object.values(tenantRoles)) {
      candidates.push(...roles);
    }
  }

  for (const role of ROLE_PRIORITY) {
    if (candidates.includes(role)) return role;
  }
  return null;
}

/**
 * Decide where to send a user after successful authentication, based on the
 * role claims in their JWT. Used by SignInForm, AuthCallback, and
 * ResetPassword so a super admin always lands on the super console and
 * tenant users always land on the admin surface, regardless of which page
 * the form happened to be on.
 *
 *   super_admin  → /super/dashboard
 *   tenant_owner → /admin/dashboard
 *   tenant_staff → /admin/dashboard
 *   consignor    → /admin/dashboard   (consignor portal lands later)
 *   (no role)    → /unauthorized
 */
export function destinationForClaims(claims: AppClaims): string {
  if (claims.isSuperAdmin) return '/super/dashboard';
  if (claims.appRole === null) return '/unauthorized';
  return '/admin/dashboard';
}

export function readClaims(session: Session | null): AppClaims {
  const empty: AppClaims = {
    appRole: null,
    tenantId: null,
    isSuperAdmin: false,
    tenantRoles: {},
  };
  if (!session) return empty;

  try {
    const [, payload] = session.access_token.split('.');
    if (!payload) return empty;
    const decoded = JSON.parse(atob(payload)) as JwtPayload;
    const meta = decoded.app_metadata ?? {};

    const isSuperAdmin = meta.is_super_admin === true;
    const tenantId = meta.tenant_id ?? null;

    // Sanitize tenant_roles — only keep recognized role strings.
    const rawRoles = meta.tenant_roles ?? {};
    const tenantRoles: Record<string, AppRole[]> = {};
    for (const [key, value] of Object.entries(rawRoles)) {
      if (!Array.isArray(value)) continue;
      const filtered = value.filter((r: string): r is AppRole =>
        (KNOWN_ROLES as readonly string[]).includes(r),
      );
      if (filtered.length > 0) tenantRoles[key] = filtered;
    }

    const appRole = pickPrimaryRole(isSuperAdmin, tenantRoles, tenantId);

    return { appRole, tenantId, isSuperAdmin, tenantRoles };
  } catch {
    return empty;
  }
}
